import React from 'react';
import PropTypes from 'prop-types';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const Header = ({ column, isSortable }) => (
  <span 
    className="react-table__column-header" 
    style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '6px', 
      width: '100%', 
      justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start' 
    }}
  >
    <span className={isSortable ? 'react-table__column-header-text sortable' : 'react-table__column-header-text'}>
      {column.render('Header')}
    </span>
    {isSortable && column.canSort && <Sorting column={column} />}
  </span>
);

Header.propTypes = {
  column: PropTypes.shape({
    Header: PropTypes.string,
    disableGlobalFilter: PropTypes.bool,
    accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    render: PropTypes.func,
    canSort: PropTypes.bool,
    align: PropTypes.string,
  }).isRequired,
  isSortable: PropTypes.bool.isRequired,
};

const Sorting = ({ column }) => (
  <span className="react-table__column-header-sort-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
    {column.isSortedDesc === undefined ? (
      <UnfoldMoreIcon style={{ fontSize: '0.85rem', opacity: 0.4 }} />
    ) : (
      <span>
        {column.isSortedDesc
          ? <KeyboardArrowDownIcon style={{ fontSize: '0.9rem', color: '#6366f1' }} />
          : <KeyboardArrowUpIcon style={{ fontSize: '0.9rem', color: '#6366f1' }} />}
      </span>
    )}
  </span>
);

Sorting.propTypes = {
  column: PropTypes.shape({
    Header: PropTypes.string,
    disableGlobalFilter: PropTypes.bool,
    accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    isSorted: PropTypes.bool,
    isSortedDesc: PropTypes.bool,
  }).isRequired,
};

const ReactTableHeader = ({ headerGroups, isResizable, isSortable }) => {
  const getHeaderPropsCombined = (column) => {
    const defaultProps = column.getHeaderProps() || {};
    
    // Sort props
    const sortProps = (isSortable && column.getSortByToggleProps) 
      ? column.getSortByToggleProps() 
      : {};
      
    // Resizer layout styles if isResizable
    const isFlexLayout = defaultProps.style?.display === 'flex';
    
    const mergedStyle = {
      ...defaultProps.style,
      ...sortProps.style,
      textAlign: column.align || 'left',
      padding: '12px 16px',
      fontSize: '11px',
      fontWeight: 700,
      color: '#475569',
      backgroundColor: '#f8fafc',
      borderBottom: '2px solid #cbd5e1',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      position: 'sticky',
      top: 0,
      zIndex: 10,
      display: isFlexLayout ? 'flex' : 'table-cell',
      alignItems: 'center',
      justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start',
      verticalAlign: 'middle',
      transition: 'background-color 0.2s',
      ...(column.width ? { width: column.width } : {}),
    };

    return {
      ...defaultProps,
      ...sortProps,
      style: mergedStyle
    };
  };

  return (
    <thead className="react-table__header">
      {headerGroups.map(headerGroup => (
        <tr {...headerGroup.getHeaderGroupProps()} className="react-table__header-row">
          {headerGroup.headers.map(column => (
            <th
              {...getHeaderPropsCombined(column)}
              className="react-table__header-cell"
            >
              <Header
                column={column}
                isSortable={isSortable}
              />
              {isResizable && (
                <div {...column.getResizerProps()} className={`resizer ${column.isResizing && 'isResizing'}`} />
              )}
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
};

ReactTableHeader.propTypes = {
  headerGroups: PropTypes.arrayOf(PropTypes.shape({
    headers: PropTypes.arrayOf(PropTypes.shape()),
    getHeaderGroupProps: PropTypes.func,
    getFooterGroupProps: PropTypes.func,
  })).isRequired,
  isResizable: PropTypes.bool.isRequired,
  isSortable: PropTypes.bool.isRequired,
};

export default ReactTableHeader;
